import type { ComponentType } from "react";
import { useEditor, useNode } from "@craftjs/core";
import { craftResolver } from "@/components/craft/resolver";
import {
  craftBlockLabel,
  resolveCraftBlockKey,
  resolveCraftBlockSettings,
} from "@/lib/craft/resolve-block-name";
import type { BlockSettingsTabProps } from "./BreakpointSwitcher";

export function useSelectedNodeInfo() {
  return useEditor((state, query) => {
    const selectedId = query.getEvent("selected").first();
    if (!selectedId) return null;

    const stateNode = state.nodes[selectedId];
    if (!stateNode) return null;

    let resolvedName = selectedId === "ROOT" ? ("ContainerBlock" as const) : resolveCraftBlockKey(stateNode);
    let name = selectedId === "ROOT" ? "Page" : craftBlockLabel(stateNode);

    let settingsComponent: ComponentType<BlockSettingsTabProps> | undefined;

    try {
      const craftNode = query.node(selectedId).get();
      if (selectedId !== "ROOT") {
        resolvedName = resolveCraftBlockKey(craftNode) ?? resolvedName;
        name = craftBlockLabel(craftNode) || name;
      }
      settingsComponent =
        (craftNode.related?.settings as ComponentType<BlockSettingsTabProps> | undefined) ??
        resolveCraftBlockSettings(resolvedName);
    } catch {
      settingsComponent = resolveCraftBlockSettings(resolvedName);
    }

    if (!settingsComponent && resolvedName) {
      settingsComponent = craftResolver[resolvedName]?.craft?.related?.settings as
        | ComponentType<BlockSettingsTabProps>
        | undefined;
    }

    return {
      id: selectedId,
      name,
      resolvedName,
      props: stateNode.data.props as Record<string, unknown>,
      hidden: stateNode.data.hidden,
      settingsComponent,
    };
  });
}

export function useSelectedBlockProps<T extends Record<string, unknown>>() {
  const { id, setProp, props } = useNode((node) => ({
    props: node.data.props as T,
  }));

  return {
    id,
    props: props ?? ({} as T),
    setProp: (fn: (p: T) => void) => setProp(fn),
    setProps: (patch: Partial<T>) =>
      setProp((p: T) => {
        Object.assign(p, patch);
      }),
  };
}
